import React from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';

// Képek importálása (Cseréld ki a saját útvonaladra, ha szükséges)
import CsillagtanodaImg from "../assets/images/csillagtanoda.jpeg";
import DigitalWeddingCardImg from "../assets/images/dwc.png"; 

import { 
  Container, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Box, 
  Button,
  useTheme
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// --- Animációk és Stílusok ---
const backgroundMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const StyledProjectCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'darkMode' && prop !== 'isSoon',
})<{ darkMode: boolean; isSoon?: boolean }>(({ darkMode, isSoon }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '24px',
  border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
  background: isSoon 
    ? (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')
    : (darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #1a1a2e 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #e0e7ff 90%)'),
  backgroundSize: '200% 200%',
  animation: isSoon ? 'none' : `${backgroundMove} 10s ease infinite`,
  backdropFilter: 'blur(10px)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  color: darkMode ? 'white' : '#1a237e',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.6)' : '0 12px 30px rgba(0,0,0,0.1)',
  },
}));

const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain', // Nem vágja le a logók szélét
  display: 'block',
  padding: '15px', 
});

const ImageWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '220px', 
  overflow: 'hidden',
  borderTopLeftRadius: '24px',
  borderTopRightRadius: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
}));

// --- Fő Komponens ---
const Projects: React.FC = () => {
  const isAuthenticated = !!Cookies.get('token');
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  // Projekt adatok tömbje
  const myProjects = [
    {
      title: "Digital Wedding Card",
      description: "Elegant digital wedding invitation system with RSVP management and personalized guest experiences.",
      image: DigitalWeddingCardImg,
      link: "https://digitalweddingcard.com"
    },
    {
      title: "Csillagtanoda",
      description: "Educational platform providing professional courses and interactive learning materials.",
      image: CsillagtanodaImg,
      link: "https://csillagtanoda.hu"
    }
  ];

  // Link megnyitása új lapon
  const handleNavigation = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      height: '100vh', 
      width: '100vw', 
      overflowX: 'hidden',
      overflowY: { xs: 'auto', md: 'hidden' }, // Csak mobilon lehet görgetni
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Háttér: Particles */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
        <Particles />
      </div>

      {/* Navbar kezelés */}
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      {/* Tartalom Konténer - Középre igazítás asztalin, fentről indul mobilon */}
      <Box sx={{ 
        flexGrow: 3, 
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' }, 
        justifyContent: 'center',
        paddingTop: { xs: '90px', md: '0px' },
        paddingBottom: { xs: 4, md: 25 } 
      }}>
        {/* maxWidth="lg" biztosítja, hogy pontosan akkora legyen, mint a többi oldalon */}
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              mb: { xs: 3, md: 5 },
              fontSize: { xs: '2rem', md: '3.4rem' },
              color: darkMode ? 'white' : '#1a237e'
            }}
          >
            My Projects
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {/* Végigmegyünk a projekteken */}
            {myProjects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledProjectCard darkMode={darkMode}>
                  <ImageWrapper>
                    <StyledImage src={project.image} alt={project.title} />
                  </ImageWrapper>
                  
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {project.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 3, flexGrow: 1, minHeight: '60px' }}>
                      {project.description}
                    </Typography>
                    <Button 
                      variant="contained" 
                      fullWidth
                      onClick={() => handleNavigation(project.link)}
                      sx={{ 
                        borderRadius: '12px', 
                        py: 1.5,
                        fontWeight: 'bold',
                        // Sötét módban mélyvörös, világosban tengerészkék
                        bgcolor: darkMode ? '#5e0b0b' : '#1a237e',
                        color: 'white',
                        border: darkMode ? '1px solid rgba(255, 0, 0, 0.3)' : 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          bgcolor: darkMode ? '#8a0e0e' : '#0d1642',
                          boxShadow: darkMode ? '0 0 15px rgba(94, 11, 11, 0.6)' : 'none',
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      View Project
                    </Button>
                  </CardContent>
                </StyledProjectCard>
              </Grid>
            ))}

            {/* A "Soon..." Kártya */}
            <Grid item xs={12} sm={6} md={4}>
              <StyledProjectCard darkMode={darkMode} isSoon={true}>
                <Box 
                  sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: { xs: '250px', md: '400px' }
                  }}
                >
                  <Typography variant="h4" sx={{ opacity: 0.2, fontWeight: 'bold', letterSpacing: 4 }}>
                    Soon...
                  </Typography>
                </Box>
              </StyledProjectCard>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Projects;